import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Calendar, User as UserIcon } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import HeaderV4 from "@/components/header-v4/HeaderV4";
import FooterV4 from "@/components/footer-v4/FooterV4";

const APP_URL = process.env.REACT_APP_BACKEND_URL || "";
const V4_ENABLED = (process.env.REACT_APP_HEADER_V4_ENABLED ?? "true") !== "false";

function readingTime(md) {
    if (!md) return 1;
    const words = md.split(/\s+/).length;
    return Math.max(1, Math.round(words / 220));
}

function formatDate(iso, lang = "en") {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(lang, {
            year: "numeric", month: "long", day: "numeric",
        });
    } catch {
        return iso.slice(0, 10);
    }
}

export default function BlogPost() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);
    const [status, setStatus] = useState("loading"); // loading|ready|notfound

    useEffect(() => {
        (async () => {
            setStatus("loading");
            try {
                const data = await api.blogPost(slug);
                setPost(data.post);
                setRelated(data.related || []);
                setStatus("ready");
            } catch (e) {
                if (e.status === 404) setStatus("notfound");
                else setStatus("notfound");
            }
        })();
    }, [slug]);

    const jsonLd = useMemo(() => {
        if (!post) return null;
        return {
            "@context": "https://schema.org",
            "@type": post.structured_data_type || "BlogPosting",
            headline: post.title,
            description: post.meta_description || post.excerpt || "",
            image: post.og_image || `${APP_URL}/og-default.png`,
            author: { "@type": "Person", name: post.author_name || "Global Lead Suite" },
            publisher: {
                "@type": "Organization",
                name: "Global Lead Suite",
                logo: { "@type": "ImageObject", url: `${APP_URL}/logo.png` },
            },
            datePublished: post.published_at,
            dateModified: post.updated_at || post.published_at,
            mainEntityOfPage: `${APP_URL}/blog/${post.slug}`,
            inLanguage: post.locale || "en",
            keywords: (post.tags || []).join(", "),
        };
    }, [post]);

    if (status === "loading") {
        return (
            <div data-testid="blogpost-loading" className="min-h-screen bg-[var(--color-bg)] text-text-secondary p-16">
                …
            </div>
        );
    }
    if (status === "notfound") {
        return (
            <div className="min-h-screen bg-[var(--color-bg)] p-16 text-center">
                <SEO title="Not found" noindex />
                <p data-testid="blogpost-notfound" className="text-text-secondary">
                    {t("errors.not_found")}
                </p>
                <button
                    onClick={() => navigate("/blog")}
                    className="mt-4 text-[var(--color-primary)]"
                >
                    {t("blog.back_to_index")}
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main">
            <SEO
                title={post.meta_title || post.title}
                description={post.meta_description || post.excerpt}
                canonical={post.canonical_url || `/blog/${post.slug}`}
                image={post.og_image}
                type="article"
                locale={post.locale}
                article={{
                    publishedTime: post.published_at,
                    modifiedTime: post.updated_at,
                    author: post.author_name,
                    tags: post.tags || [],
                }}
                jsonLd={jsonLd}
            />

            {V4_ENABLED ? (
                <HeaderV4 />
            ) : (
                <header className="sticky top-0 z-30 border-b border-[var(--color-border)] bg-[var(--color-bg)]/85 backdrop-blur-xl">
                    <div className="mx-auto max-w-4xl flex items-center justify-between px-6 py-4">
                        <Link
                            to="/blog"
                            data-testid="blogpost-back"
                            className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-main transition-colors"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            {t("blog.back_to_index")}
                        </Link>
                        <LanguageSwitcher compact />
                    </div>
                </header>
            )}

            <article className="mx-auto max-w-3xl px-6 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-6">
                        <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5">
                            {post.category_slug}
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(post.published_at, i18n.language)}
                        </span>
                        <span>· {readingTime(post.content_markdown)} {t("blog.read_time")}</span>
                    </div>
                    <h1
                        data-testid="blogpost-title"
                        className="text-4xl sm:text-5xl font-semibold leading-tight tracking-tight"
                    >
                        {post.title}
                    </h1>
                    {post.subtitle && (
                        <p className="mt-4 text-lg text-text-secondary">{post.subtitle}</p>
                    )}
                    <div className="mt-6 flex items-center gap-3 text-sm text-text-secondary">
                        <UserIcon className="h-4 w-4" />
                        <span>{post.author_name || "Global Lead Suite"}</span>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.15 }}
                    className="prose prose-invert mt-10 max-w-none prose-headings:tracking-tight prose-headings:text-text-main prose-p:text-text-main/90 prose-a:text-[var(--color-secondary)] prose-strong:text-text-main prose-code:text-[var(--color-secondary)] prose-code:bg-[var(--color-panel-ai)] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:bg-[var(--color-panel-ai)] prose-pre:border prose-pre:border-[var(--color-border)]"
                    data-testid="blogpost-body"
                >
                    <ReactMarkdown>{post.content_markdown || ""}</ReactMarkdown>
                </motion.div>

                {post.tags?.length ? (
                    <div className="mt-12 flex flex-wrap gap-2 border-t border-[var(--color-border)] pt-8">
                        {post.tags.map((tag) => (
                            <span
                                key={tag}
                                className="rounded-full border border-[var(--color-border)] px-3 py-1 text-xs text-text-secondary"
                            >
                                #{tag}
                            </span>
                        ))}
                    </div>
                ) : null}
            </article>

            {related?.length > 0 && (
                <section className="border-t border-[var(--color-border)] py-16">
                    <div className="mx-auto max-w-4xl px-6">
                        <h2 className="text-xl font-semibold mb-6">{t("blog.related")}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {related.map((r) => (
                                <Link
                                    key={r.slug}
                                    to={`/blog/${r.slug}`}
                                    data-testid={`blogpost-related-${r.slug}`}
                                    className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 hover:border-[var(--color-primary)]/50 transition-colors"
                                >
                                    <h3 className="text-sm font-medium leading-snug line-clamp-2">
                                        {r.title}
                                    </h3>
                                    {r.excerpt && (
                                        <p className="mt-2 text-xs text-text-secondary line-clamp-2">
                                            {r.excerpt}
                                        </p>
                                    )}
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
            {V4_ENABLED && <FooterV4 />}
        </div>
    );
}
